import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  critical: "Critical",
  out: "Out",
  mostly: "Mostly",
  substantial: "Substantial",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("space_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [ebookLeads, setEbookLeads] = useState([]);
  const [diagTokens, setDiagTokens] = useState([]);
  const [diagResults, setDiagResults] = useState([]);
  const [recordingTokens, setRecordingTokens] = useState([]);
  const [offlineCatalog, setOfflineCatalog] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("quiz"); // quiz | ebook | diag-access | diag-results | recording | offline | purchases | conversations
  const [dripMsg, setDripMsg] = useState("");

  // Grant Diagnostic access
  const [grantName, setGrantName] = useState("");
  const [grantEmail, setGrantEmail] = useState("");
  const [grantMsg, setGrantMsg] = useState("");
  const [grantedLink, setGrantedLink] = useState("");
  const [granting, setGranting] = useState(false);

  // Grant Recording access
  const [recName, setRecName] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [recUrl, setRecUrl] = useState("");
  const [recNote, setRecNote] = useState("");
  const [recMsg, setRecMsg] = useState("");
  const [recGrantedLink, setRecGrantedLink] = useState("");
  const [recGranting, setRecGranting] = useState(false);
  const [copiedToken, setCopiedToken] = useState("");

  // Global search across all collections
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    document.title = "S.P.A.C.E. Academy — Admin";
  }, []);

  async function loadAll(authToken) {
    setLoading(true);
    setError("");
    try {
      const [quizRes, ebookRes, diagTokRes, diagResRes, recRes, catalogRes, purchasesRes, convRes] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/ebook-requests`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/diagnostic-tokens`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/diagnostic-results`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/recording-tokens`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/offline-pdf-catalog`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/purchases`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/architect-conversations`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      setSubmissions(quizRes.data.submissions || []);
      setEbookLeads(ebookRes.data.requests || []);
      setDiagTokens(diagTokRes.data.tokens || []);
      setDiagResults(diagResRes.data.results || []);
      setRecordingTokens(recRes.data.tokens || []);
      setOfflineCatalog(catalogRes.data.pillars || []);
      setPurchases(purchasesRes.data.purchases || []);
      setConversations(convRes.data.conversations || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Session expired. Please log in again.");
        setToken("");
        localStorage.removeItem("space_admin_token");
      } else {
        setError("Could not load data.");
      }
    }
    setLoading(false);
  }

  useEffect(() => {
    if (token) loadAll(token);
  }, [token]);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      localStorage.setItem("space_admin_token", t);
      setToken(t);
      setPassword("");
    } catch (err) {
      setError("Invalid password.");
    }
  }

  function logout() {
    localStorage.removeItem("space_admin_token");
    setToken("");
    setSubmissions([]);
    setEbookLeads([]);
    setDiagTokens([]);
    setDiagResults([]);
    setRecordingTokens([]);
  }

  function downloadCSV() {
    if (!token) return;
    const url = `${API}/admin/submissions.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  }

  async function runDrips() {
    setDripMsg("Running…");
    try {
      const res = await axios.post(
        `${API}/admin/run-drips`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      setDripMsg(`Drip job: ${d.sent} sent, ${d.failed} failed (${d.candidates} candidates).`);
      await loadAll(token);
    } catch {
      setDripMsg("Drip job failed.");
    }
  }

  async function grantAccess(e) {
    e.preventDefault();
    setGrantMsg("");
    setGrantedLink("");
    if (!grantName.trim() || !grantEmail.trim()) {
      setGrantMsg("Name and email required.");
      return;
    }
    setGranting(true);
    try {
      const res = await axios.post(
        `${API}/admin/grant-diagnostic-access`,
        { name: grantName.trim(), email: grantEmail.trim(), send_email: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGrantedLink(res.data.diagnostic_url);
      setGrantMsg(`Magic link generated for ${res.data.email}. Copy the link below and paste it into your email.`);
      setGrantName("");
      setGrantEmail("");
      await loadAll(token);
    } catch (err) {
      setGrantMsg(err?.response?.data?.detail || "Could not grant access.");
    }
    setGranting(false);
  }

  async function grantRecordingAccess(e) {
    e.preventDefault();
    setRecMsg("");
    setRecGrantedLink("");
    if (!recName.trim() || !recEmail.trim() || !recUrl.trim()) {
      setRecMsg("Name, email, and recording URL are required.");
      return;
    }
    setRecGranting(true);
    try {
      const res = await axios.post(
        `${API}/admin/grant-recording-access`,
        {
          name: recName.trim(),
          email: recEmail.trim(),
          recording_url: recUrl.trim(),
          note: recNote.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecGrantedLink(res.data.magic_link);
      setRecMsg(`Recording link generated for ${res.data.email}. Copy the link below into your email.`);
      setRecName("");
      setRecEmail("");
      setRecUrl("");
      setRecNote("");
      await loadAll(token);
    } catch (err) {
      setRecMsg(err?.response?.data?.detail || "Could not generate recording link.");
    }
    setRecGranting(false);
  }

  async function revokeRecording(tk, email) {
    if (!window.confirm(`Revoke the recording link for ${email}? They will no longer be able to view the recording.`)) return;
    try {
      await axios.delete(`${API}/admin/recording-tokens/${tk}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadAll(token);
    } catch {
      setRecMsg("Could not revoke link.");
    }
  }

  function downloadIdentityMap(id) {
    const url = `${API}/admin/diagnostic-results/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState("");
  const [resendingEbookId, setResendingEbookId] = useState(null);
  const [ebookResendMsg, setEbookResendMsg] = useState("");

  async function resendEbook(id, email) {
    if (!window.confirm(`Re-email the free eBook PDF link to ${email}?`)) return;
    setResendingEbookId(id);
    setEbookResendMsg("");
    try {
      const res = await axios.post(
        `${API}/admin/ebook-requests/${id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEbookResendMsg(`eBook re-sent to ${res.data.to}.`);
      await loadAll(token);
    } catch (err) {
      setEbookResendMsg(err?.response?.data?.detail || "Resend failed.");
    }
    setResendingEbookId(null);
  }

  async function resendIdentityMap(id, email) {
    if (!window.confirm(`Re-email the Identity Map PDF to ${email}?`)) return;
    setResendingId(id);
    setResendMsg("");
    try {
      const res = await axios.post(
        `${API}/admin/diagnostic-results/${id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResendMsg(`Identity Map re-sent to ${res.data.to}.`);
      await loadAll(token);
    } catch (err) {
      setResendMsg(err?.response?.data?.detail || "Resend failed.");
    }
    setResendingId(null);
  }

  function copyLink(url, tk) {
    navigator.clipboard?.writeText(url);
    if (tk) {
      setCopiedToken(tk);
      setTimeout(() => setCopiedToken(""), 1500);
    }
  }

  // ---- Unified search across all collections ----
  const q = searchQuery.trim().toLowerCase();
  const isSearching = q.length > 0;

  const matchEmail = (val) => (val || "").toLowerCase().includes(q);
  const matchAny = (rec) => matchEmail(rec.email) || matchEmail(rec.name);

  const searchResults = isSearching
    ? {
        quiz: submissions.filter(matchAny),
        ebook: ebookLeads.filter(matchAny),
        diagTokens: diagTokens.filter(matchAny),
        diagResults: diagResults.filter(matchAny),
        recordings: recordingTokens.filter(matchAny),
      }
    : null;

  const searchTotal = searchResults
    ? searchResults.quiz.length +
      searchResults.ebook.length +
      searchResults.diagTokens.length +
      searchResults.diagResults.length +
      searchResults.recordings.length
    : 0;

  if (!token) {
    return (
      <div className="space-admin" data-testid="admin-root">
        <div className="admin-wrap">
          <h1>S.P.A.C.E. Admin</h1>
          <p className="admin-sub">Quiz lead dashboard · Restricted</p>
          <form onSubmit={handleLogin} className="admin-card" data-testid="admin-login-form">
            <label style={{ display: "block", marginBottom: 8, fontSize: 13, letterSpacing: "0.1em", textTransform: "uppercase", color: "#c7bfa8" }}>
              Admin Password
            </label>
            <input
              type="password"
              className="admin-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              data-testid="admin-password-input"
              autoFocus
            />
            <button type="submit" className="admin-btn" data-testid="admin-login-btn" style={{ width: "100%" }}>
              Enter
            </button>
            {error && <p className="error-msg" data-testid="admin-login-error">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-admin" data-testid="admin-dashboard">
      <div className="admin-wrap">
        <h1>S.P.A.C.E. Academy — Admin</h1>
        <p className="admin-sub">All leads · Most recent first</p>

        {/* Global search across all collections */}
        <div className="admin-search-bar" data-testid="admin-search-bar">
          <input
            type="search"
            className="admin-input admin-search-input"
            placeholder="🔍 Search by email or name across all records…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="admin-search-input"
          />
          {isSearching && (
            <button
              type="button"
              className="admin-btn outline admin-search-clear"
              onClick={() => setSearchQuery("")}
              data-testid="admin-search-clear-btn"
            >
              Clear
            </button>
          )}
        </div>

        {isSearching ? (
          <SearchResultsPanel
            query={searchQuery}
            total={searchTotal}
            results={searchResults}
            copyLink={copyLink}
            copiedToken={copiedToken}
            onDownloadPdf={downloadIdentityMap}
            onResendPdf={resendIdentityMap}
            onResendEbook={resendEbook}
            onRevokeRecording={revokeRecording}
            resendingId={resendingId}
            resendingEbookId={resendingEbookId}
            adminToken={token}
          />
        ) : (
        <>
        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={`admin-tab ${tab === "quiz" ? "active" : ""}`} onClick={() => setTab("quiz")} data-testid="tab-quiz">
            Quiz Leads <span className="admin-tab-count">{submissions.length}</span>
          </button>
          <button className={`admin-tab ${tab === "ebook" ? "active" : ""}`} onClick={() => setTab("ebook")} data-testid="tab-ebook">
            eBook Leads <span className="admin-tab-count">{ebookLeads.length}</span>
          </button>
          <button className={`admin-tab ${tab === "diag-access" ? "active" : ""}`} onClick={() => setTab("diag-access")} data-testid="tab-diag-access">
            Diagnostic Access <span className="admin-tab-count">{diagTokens.length}</span>
          </button>
          <button className={`admin-tab ${tab === "diag-results" ? "active" : ""}`} onClick={() => setTab("diag-results")} data-testid="tab-diag-results">
            Completed Diagnostics <span className="admin-tab-count">{diagResults.length}</span>
          </button>
          <button className={`admin-tab ${tab === "recording" ? "active" : ""}`} onClick={() => setTab("recording")} data-testid="tab-recording">
            Recording Access ($497) <span className="admin-tab-count">{recordingTokens.length}</span>
          </button>
          <button className={`admin-tab ${tab === "offline" ? "active" : ""}`} onClick={() => setTab("offline")} data-testid="tab-offline">
            Offline Pillar PDFs <span className="admin-tab-count">{offlineCatalog.length}</span>
          </button>
          <button className={`admin-tab ${tab === "purchases" ? "active" : ""}`} onClick={() => setTab("purchases")} data-testid="tab-purchases">
            Purchases <span className="admin-tab-count">{purchases.length}</span>
          </button>
          <button className={`admin-tab ${tab === "conversations" ? "active" : ""}`} onClick={() => setTab("conversations")} data-testid="tab-conversations">
            Architect Chats <span className="admin-tab-count">{conversations.length}</span>
          </button>
        </div>

        <div className="admin-toolbar">
          <button className="admin-btn" onClick={() => loadAll(token)} data-testid="admin-refresh-btn">Refresh</button>
          {tab === "quiz" && (
            <button className="admin-btn outline" onClick={downloadCSV} data-testid="admin-csv-btn">Export Quiz CSV</button>
          )}
          {tab === "ebook" && (
            <button className="admin-btn outline" onClick={runDrips} disabled={dripMsg === "Running…"} data-testid="admin-run-drips-btn">
              Run Day-3 Drips Now
            </button>
          )}
          <button className="admin-btn outline" onClick={logout} data-testid="admin-logout-btn">Logout</button>
          <span style={{ marginLeft: "auto", color: "#c7bfa8", fontSize: 14 }} data-testid="admin-count">
            {loading ? "Loading…" : tab === "quiz" ? `${submissions.length} quiz leads`
              : tab === "ebook" ? `${ebookLeads.length} eBook leads`
              : tab === "diag-access" ? `${diagTokens.length} access tokens`
              : tab === "recording" ? `${recordingTokens.length} recording links`
              : tab === "offline" ? `${offlineCatalog.length} sellable mini-product PDFs`
              : `${diagResults.length} completed diagnostics`}
          </span>
        </div>

        {dripMsg && tab === "ebook" && (
          <p style={{ color: "#f5d76e", fontSize: 13, marginBottom: 16 }} data-testid="admin-drip-msg">{dripMsg}</p>
        )}

        {error && <p className="error-msg" data-testid="admin-error">{error}</p>}

        {tab === "quiz" && (
          <>
            <ManualQuizImport adminToken={token} onImported={() => loadAll(token)} />
            <table className="admin-table" data-testid="admin-table-quiz">
              <thead>
                <tr><th>Date</th><th>Name</th><th>Email</th><th>Tier</th><th>Total</th><th>S</th><th>P</th><th>A</th><th>C</th><th>E</th><th>Misaligned</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {submissions.length === 0 && !loading && (<tr><td colSpan="12" className="empty-state">No quiz leads yet.</td></tr>)}
                {submissions.map((s) => (
                  <tr key={s.id} data-testid={`admin-quiz-row-${s.id}`}>
                    <td>{(s.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{s.name}{s.manually_imported && <span style={{ color: "#f5d76e", fontSize: 9, marginLeft: 6 }}>· imported</span>}</td><td>{s.email}</td>
                    <td><span className={`tier-pill ${s.tier}`}>{TIER_LABELS[s.tier] || s.tier}</span></td>
                    <td>{s.total}/15</td>
                    <td>{s.scores?.S}</td><td>{s.scores?.P}</td><td>{s.scores?.A}</td><td>{s.scores?.C}</td><td>{s.scores?.E}</td>
                    <td>{(s.misaligned || []).join(", ")}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <a
                        href={`${API}/admin/submissions/${s.id}/pdf?token=${encodeURIComponent(token)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px", textDecoration: "none" }}
                        data-testid={`admin-quiz-pdf-${s.id}`}
                      >
                        Download PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "ebook" && (
          <>
            {ebookResendMsg && (
              <p style={{ color: "#f5d76e", fontSize: 13, marginBottom: 16 }} data-testid="admin-ebook-resend-msg">
                {ebookResendMsg}
              </p>
            )}
            <table className="admin-table" data-testid="admin-table-ebook">
              <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Quiz Taken?</th><th>Day-3 Drip</th><th>Day-7 Drip</th><th>Last Resent</th><th>Actions</th></tr></thead>
              <tbody>
                {ebookLeads.length === 0 && !loading && (<tr><td colSpan="8" className="empty-state">No eBook downloads yet.</td></tr>)}
                {ebookLeads.map((s) => (
                  <tr key={s.id} data-testid={`admin-ebook-row-${s.id}`}>
                    <td>{(s.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{s.name}</td><td>{s.email}</td>
                    <td><span className={`tier-pill ${s.quiz_taken ? "substantial" : "out"}`}>{s.quiz_taken ? "Yes" : "Not yet"}</span></td>
                    <td><span className={`tier-pill drip-${s.drip_day3_status || "pending"}`}>{s.drip_day3_status || "pending"}</span></td>
                    <td><span className={`tier-pill drip-${s.drip_day7_status || "pending"}`}>{s.drip_day7_status || "pending"}</span></td>
                    <td style={{ fontSize: 11 }}>
                      {s.ebook_last_resent_at ? (s.ebook_last_resent_at || "").slice(0, 16).replace("T", " ") : "—"}
                      {s.ebook_resend_history?.length ? <div style={{ color: "#c7bfa8" }}>({s.ebook_resend_history.length}×)</div> : null}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                        onClick={() => copyLink(s.download_url, `ebook-${s.id}`)}
                        data-testid={`admin-ebook-copy-${s.id}`}
                        title="Copy the eBook download URL for manual paste"
                      >
                        {copiedToken === `ebook-${s.id}` ? "Copied ✓" : "Copy Link"}
                      </button>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px" }}
                        onClick={() => resendEbook(s.id, s.email)}
                        disabled={resendingEbookId === s.id}
                        data-testid={`admin-ebook-resend-${s.id}`}
                        title="Re-send the eBook delivery email via Resend"
                      >
                        {resendingEbookId === s.id ? "Sending…" : "Resend Email"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "diag-access" && (
          <>
            <form onSubmit={grantAccess} className="grant-form" data-testid="grant-access-form">
              <h3 className="grant-form-title">Generate Diagnostic Access Link ($197 Tier 02)</h3>
              <p className="grant-form-sub">
                After you receive the $197 donation notification, generate a one-time magic
                link here. <strong style={{ color: "#f5d76e" }}>The system does not email it.</strong>{" "}
                Copy the link below and paste it into your own email to the client.
              </p>
              <div className="grant-form-row">
                <input type="text" className="admin-input" placeholder="Buyer's name" value={grantName} onChange={(e) => setGrantName(e.target.value)} data-testid="grant-name-input" />
                <input type="email" className="admin-input" placeholder="Buyer's email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} data-testid="grant-email-input" />
                <button type="submit" className="admin-btn" disabled={granting} data-testid="grant-submit-btn">
                  {granting ? "Generating…" : "Generate Magic Link"}
                </button>
              </div>
              {grantMsg && <p className="grant-form-msg" data-testid="grant-msg">{grantMsg}</p>}
              {grantedLink && (
                <div className="magic-link-box" data-testid="grant-magic-link-box">
                  <p className="magic-link-label">DIAGNOSTIC MAGIC LINK — One-Time Use</p>
                  <div className="magic-link-row">
                    <input
                      type="text"
                      className="admin-input magic-link-input"
                      value={grantedLink}
                      readOnly
                      onFocus={(e) => e.target.select()}
                      data-testid="grant-magic-link-input"
                    />
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => copyLink(grantedLink, "diag-new")}
                      data-testid="grant-copy-link-btn"
                    >
                      {copiedToken === "diag-new" ? "Copied ✓" : "Copy Link"}
                    </button>
                  </div>
                </div>
              )}
            </form>

            <table className="admin-table" data-testid="admin-table-diag-access">
              <thead><tr><th>Issued</th><th>Name</th><th>Email</th><th>Status</th><th>Used At</th><th>Magic Link</th></tr></thead>
              <tbody>
                {diagTokens.length === 0 && !loading && (<tr><td colSpan="6" className="empty-state">No access tokens issued yet.</td></tr>)}
                {diagTokens.map((t) => (
                  <tr key={t.token} data-testid={`admin-token-row-${t.token}`}>
                    <td>{(t.issued_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{t.name}</td><td>{t.email}</td>
                    <td><span className={`tier-pill ${t.status === "used" ? "substantial" : "out"}`}>{t.status}</span></td>
                    <td>{t.used_at ? (t.used_at || "").slice(0, 19).replace("T", " ") : "—"}</td>
                    <td>
                      <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px" }} onClick={() => copyLink(t.diagnostic_url, t.token)} data-testid={`admin-token-copy-${t.token}`}>
                        {copiedToken === t.token ? "Copied ✓" : "Copy Link"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "recording" && (
          <>
            <form onSubmit={grantRecordingAccess} className="grant-form" data-testid="grant-recording-form">
              <h3 className="grant-form-title">Generate 30-Min Recording Link ($497 Tier 03)</h3>
              <p className="grant-form-sub">
                Paste the recording URL (YouTube, Vimeo, Loom, Google Drive, S3, or any
                shareable link). The system generates a permanent magic link.{" "}
                <strong style={{ color: "#f5d76e" }}>Email is NOT sent automatically.</strong>{" "}
                Copy the generated link and paste it into your email to the client.
              </p>
              <div className="grant-form-row">
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Buyer's name"
                  value={recName}
                  onChange={(e) => setRecName(e.target.value)}
                  data-testid="rec-name-input"
                />
                <input
                  type="email"
                  className="admin-input"
                  placeholder="Buyer's email"
                  value={recEmail}
                  onChange={(e) => setRecEmail(e.target.value)}
                  data-testid="rec-email-input"
                />
              </div>
              <div className="grant-form-row" style={{ marginTop: 12 }}>
                <input
                  type="url"
                  className="admin-input"
                  placeholder="Recording URL (YouTube, Vimeo, Loom, MP4, Drive…)"
                  value={recUrl}
                  onChange={(e) => setRecUrl(e.target.value)}
                  style={{ flex: 2 }}
                  data-testid="rec-url-input"
                />
                <button type="submit" className="admin-btn" disabled={recGranting} data-testid="rec-submit-btn">
                  {recGranting ? "Generating…" : "Generate Magic Link"}
                </button>
              </div>
              <textarea
                className="admin-input"
                placeholder="Optional note shown to the client on the recording page"
                value={recNote}
                onChange={(e) => setRecNote(e.target.value)}
                rows={2}
                style={{ width: "100%", marginTop: 12, resize: "vertical" }}
                data-testid="rec-note-input"
              />
              {recMsg && <p className="grant-form-msg" data-testid="rec-msg">{recMsg}</p>}
              {recGrantedLink && (
                <div className="magic-link-box" data-testid="rec-magic-link-box">
                  <p className="magic-link-label">RECORDING MAGIC LINK — Unlimited Views</p>
                  <div className="magic-link-row">
                    <input
                      type="text"
                      className="admin-input magic-link-input"
                      value={recGrantedLink}
                      readOnly
                      onFocus={(e) => e.target.select()}
                      data-testid="rec-magic-link-input"
                    />
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => copyLink(recGrantedLink, "rec-new")}
                      data-testid="rec-copy-link-btn"
                    >
                      {copiedToken === "rec-new" ? "Copied ✓" : "Copy Link"}
                    </button>
                  </div>
                </div>
              )}
            </form>

            <table className="admin-table" data-testid="admin-table-recording">
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Recording URL</th>
                  <th>Views</th>
                  <th>Last Viewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recordingTokens.length === 0 && !loading && (
                  <tr><td colSpan="7" className="empty-state">No recording links generated yet.</td></tr>
                )}
                {recordingTokens.map((t) => (
                  <tr key={t.token} data-testid={`admin-rec-row-${t.token}`}>
                    <td>{(t.issued_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{t.name}</td>
                    <td>{t.email}</td>
                    <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <a href={t.recording_url} target="_blank" rel="noopener noreferrer" style={{ color: "#d4af37" }}>
                        {t.recording_url}
                      </a>
                    </td>
                    <td>
                      <span className={`tier-pill ${t.view_count > 0 ? "substantial" : "out"}`}>
                        {t.view_count || 0}
                      </span>
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {t.last_viewed_at ? (t.last_viewed_at || "").slice(0, 16).replace("T", " ") : "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                        onClick={() => copyLink(t.magic_link, t.token)}
                        data-testid={`admin-rec-copy-${t.token}`}
                      >
                        {copiedToken === t.token ? "Copied ✓" : "Copy Link"}
                      </button>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px" }}
                        onClick={() => revokeRecording(t.token, t.email)}
                        data-testid={`admin-rec-revoke-${t.token}`}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "diag-results" && (
          <>
            {resendMsg && (
              <p style={{ color: "#f5d76e", fontSize: 13, marginBottom: 16 }} data-testid="admin-resend-msg">
                {resendMsg}
              </p>
            )}
            <table className="admin-table" data-testid="admin-table-diag-results">
              <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Total</th><th>Overall Tier</th><th>S</th><th>P</th><th>A</th><th>C</th><th>E</th><th>Last Resent</th><th>Actions</th></tr></thead>
              <tbody>
                {diagResults.length === 0 && !loading && (<tr><td colSpan="12" className="empty-state">No completed diagnostics yet.</td></tr>)}
                {diagResults.map((r) => (
                  <tr key={r.id} data-testid={`admin-result-row-${r.id}`}>
                    <td>{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                    <td>{r.name}</td><td>{r.email}</td>
                    <td>{r.total_score}/300</td>
                    <td><span className="tier-pill substantial">{r.overall_tier_label}</span></td>
                    <td>{r.pillar_scores?.S}</td><td>{r.pillar_scores?.P}</td><td>{r.pillar_scores?.A}</td><td>{r.pillar_scores?.C}</td><td>{r.pillar_scores?.E}</td>
                    <td style={{ fontSize: 11 }}>
                      {r.last_resent_at ? (r.last_resent_at || "").slice(0, 16).replace("T", " ") : "—"}
                      {r.resend_history?.length ? <div style={{ color: "#c7bfa8" }}>({r.resend_history.length}×)</div> : null}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                        onClick={() => downloadIdentityMap(r.id)}
                        data-testid={`admin-result-pdf-${r.id}`}
                      >
                        Download
                      </button>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px" }}
                        onClick={() => resendIdentityMap(r.id, r.email)}
                        disabled={resendingId === r.id}
                        data-testid={`admin-result-resend-${r.id}`}
                      >
                        {resendingId === r.id ? "Sending…" : "Resend Email"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "offline" && (
          <div className="offline-pdfs-panel" data-testid="offline-pdfs-panel">
            <p className="grant-form-sub" style={{ marginBottom: 24 }}>
              Five sellable mini-product PDFs — one per pillar. Each is a complete
              standalone offline diagnostic with etymology, science, body cues,
              myth-corrections, daily practices, scripture, reflection journal,
              and a 12-question self-scoring Likert test. Designed for non-technical
              older clients to use on paper.
            </p>
            <div className="offline-pdfs-grid">
              {offlineCatalog.length === 0 && (
                <p className="empty-state">Loading catalog…</p>
              )}
              {offlineCatalog.map((p) => (
                <div className="offline-pdf-card" data-testid={`offline-pdf-card-${p.key}`} key={p.key}>
                  <div className="offline-pdf-letter">{p.key}</div>
                  <h4 className="offline-pdf-title">{p.title}</h4>
                  <p className="offline-pdf-subtitle">{p.subtitle}</p>
                  <p className="offline-pdf-tagline">&ldquo;{p.tagline}&rdquo;</p>
                  <a
                    href={`${API}/admin/offline-pdf/${p.key}?token=${encodeURIComponent(token)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-btn"
                    data-testid={`offline-pdf-download-${p.key}`}
                  >
                    Download {p.name} PDF
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "purchases" && (
          <PurchasesTab
            purchases={purchases}
            adminToken={token}
            onChange={() => loadAll(token)}
          />
        )}

        {tab === "conversations" && (
          <ConversationsTab
            conversations={conversations}
            adminToken={token}
            onChange={() => loadAll(token)}
          />
        )}
        </>
        )}
      </div>
    </div>
  );
}

// =====================================================
// ManualQuizImport — backfill a free-quiz submission that was lost
// =====================================================
function ManualQuizImport({ adminToken, onImported }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", tier: "substantial", total: 0,
    misaligned: "", timestamp: "",
  });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        tier: form.tier.toLowerCase().trim(),
        total: Number(form.total),
        misaligned: form.misaligned
          ? form.misaligned.split(",").map((x) => x.trim()).filter(Boolean)
          : [],
        timestamp: form.timestamp.trim() || null,
      };
      const res = await axios.post(
        `${API}/admin/submissions/manual-import`,
        payload,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMsg(res.data.status === "duplicate"
        ? `Already on file (id ${res.data.id.slice(0, 8)}…).`
        : `Imported. id ${res.data.id.slice(0, 8)}…`);
      setForm({ name: "", email: "", tier: "substantial", total: 0, misaligned: "", timestamp: "" });
      onImported && onImported();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Import failed.");
    }
    setBusy(false);
  }

  return (
    <div className="grant-form" style={{ marginBottom: 24 }} data-testid="manual-import-form">
      <button
        type="button"
        className="admin-btn outline"
        style={{ marginBottom: open ? 16 : 0 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="manual-import-toggle"
      >
        {open ? "Close Manual Import" : "+ Manually Import Lost Quiz Submission"}
      </button>
      {open && (
        <form onSubmit={submit}>
          <p className="grant-form-sub" style={{ marginBottom: 14 }}>
            Use this when a customer reports having taken the quiz but the record isn&apos;t
            in the table (delivery failure, server hiccup, etc.). Paste the data they
            shared in their email; the system dedupes by email+timestamp.
          </p>
          <div className="grant-form-row" style={{ gap: 10 }}>
            <input className="admin-input" placeholder="Full name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="manual-import-name" required />
            <input className="admin-input" placeholder="Email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="manual-import-email" required />
          </div>
          <div className="grant-form-row" style={{ gap: 10, marginTop: 10 }}>
            <select className="admin-input" value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              data-testid="manual-import-tier">
              <option value="substantial">Substantial</option>
              <option value="mostly">Mostly</option>
              <option value="out">Out of Alignment</option>
              <option value="critical">Critical</option>
            </select>
            <input className="admin-input" placeholder="Total score (0–15)" type="number" min="0" max="15"
              value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })}
              data-testid="manual-import-total" required />
            <input className="admin-input" placeholder="Submitted ISO (optional)"
              value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })}
              data-testid="manual-import-timestamp" />
          </div>
          <input className="admin-input" placeholder="Misaligned pillars, comma-separated (e.g., Spiritual, Entrepreneurship)"
            value={form.misaligned} onChange={(e) => setForm({ ...form, misaligned: e.target.value })}
            style={{ width: "100%", marginTop: 10 }} data-testid="manual-import-misaligned" />
          <button type="submit" className="admin-btn" disabled={busy}
            style={{ marginTop: 14 }} data-testid="manual-import-submit">
            {busy ? "Importing…" : "Import Submission"}
          </button>
          {msg && <p className="grant-form-msg" data-testid="manual-import-msg">{msg}</p>}
        </form>
      )}
    </div>
  );
}

// =====================================================
// SearchResultsPanel — unified view of all records for a given email/name
// =====================================================
function SearchResultsPanel({
  query,
  total,
  results,
  copyLink,
  copiedToken,
  onDownloadPdf,
  onResendPdf,
  onResendEbook,
  onRevokeRecording,
  resendingId,
  resendingEbookId,
  adminToken,
}) {
  function downloadDossier() {
    const url = `${API}/admin/client-dossier?q=${encodeURIComponent(query)}&token=${encodeURIComponent(adminToken)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (total === 0) {
    return (
      <div className="search-results" data-testid="search-results-empty">
        <p className="search-summary">
          No records found for <span className="search-q">&ldquo;{query}&rdquo;</span>.
        </p>
      </div>
    );
  }

  const fmtDate = (s) => (s || "").slice(0, 19).replace("T", " ");

  return (
    <div className="search-results" data-testid="search-results">
      <div className="search-header-row">
        <p className="search-summary" style={{ margin: 0, flex: 1 }}>
          Found <strong>{total}</strong> record{total === 1 ? "" : "s"} matching{" "}
          <span className="search-q">&ldquo;{query}&rdquo;</span>
        </p>
        <button
          type="button"
          className="admin-btn"
          onClick={downloadDossier}
          data-testid="export-dossier-btn"
          title="Download a single PDF containing every record for this client"
        >
          Export Dossier (PDF)
        </button>
      </div>

      {results.ebook.length > 0 && (
        <section className="search-section" data-testid="search-section-ebook">
          <h3 className="search-section-title">eBook Leads ({results.ebook.length})</h3>
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Quiz Taken?</th><th>Day-3</th><th>Day-7</th><th>Actions</th></tr></thead>
            <tbody>
              {results.ebook.map((s) => (
                <tr key={s.id}>
                  <td>{fmtDate(s.timestamp)}</td>
                  <td>{s.name}</td><td>{s.email}</td>
                  <td><span className={`tier-pill ${s.quiz_taken ? "substantial" : "out"}`}>{s.quiz_taken ? "Yes" : "Not yet"}</span></td>
                  <td><span className={`tier-pill drip-${s.drip_day3_status || "pending"}`}>{s.drip_day3_status || "pending"}</span></td>
                  <td><span className={`tier-pill drip-${s.drip_day7_status || "pending"}`}>{s.drip_day7_status || "pending"}</span></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }} onClick={() => copyLink(s.download_url, `s-ebook-${s.id}`)}>
                      {copiedToken === `s-ebook-${s.id}` ? "Copied ✓" : "Copy Link"}
                    </button>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px" }} onClick={() => onResendEbook(s.id, s.email)} disabled={resendingEbookId === s.id}>
                      {resendingEbookId === s.id ? "Sending…" : "Resend"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {results.quiz.length > 0 && (
        <section className="search-section" data-testid="search-section-quiz">
          <h3 className="search-section-title">Free Quiz Results ({results.quiz.length})</h3>
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Tier</th><th>Total</th><th>S</th><th>P</th><th>A</th><th>C</th><th>E</th></tr></thead>
            <tbody>
              {results.quiz.map((s) => (
                <tr key={s.id}>
                  <td>{fmtDate(s.timestamp)}</td>
                  <td>{s.name}</td><td>{s.email}</td>
                  <td><span className={`tier-pill ${s.tier}`}>{TIER_LABELS[s.tier] || s.tier}</span></td>
                  <td>{s.total}/15</td>
                  <td>{s.scores?.S}</td><td>{s.scores?.P}</td><td>{s.scores?.A}</td><td>{s.scores?.C}</td><td>{s.scores?.E}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {results.diagTokens.length > 0 && (
        <section className="search-section" data-testid="search-section-diag-access">
          <h3 className="search-section-title">Diagnostic Access Links ({results.diagTokens.length})</h3>
          <table className="admin-table">
            <thead><tr><th>Issued</th><th>Name</th><th>Email</th><th>Status</th><th>Used At</th><th>Magic Link</th></tr></thead>
            <tbody>
              {results.diagTokens.map((t) => (
                <tr key={t.token}>
                  <td>{fmtDate(t.issued_at)}</td>
                  <td>{t.name}</td><td>{t.email}</td>
                  <td><span className={`tier-pill ${t.status === "used" ? "substantial" : "out"}`}>{t.status}</span></td>
                  <td>{t.used_at ? fmtDate(t.used_at) : "—"}</td>
                  <td>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px" }} onClick={() => copyLink(t.diagnostic_url, `s-dt-${t.token}`)}>
                      {copiedToken === `s-dt-${t.token}` ? "Copied ✓" : "Copy Link"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {results.diagResults.length > 0 && (
        <section className="search-section" data-testid="search-section-diag-results">
          <h3 className="search-section-title">Completed Diagnostics ({results.diagResults.length})</h3>
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Total</th><th>Tier</th><th>S</th><th>P</th><th>A</th><th>C</th><th>E</th><th>Actions</th></tr></thead>
            <tbody>
              {results.diagResults.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.timestamp)}</td>
                  <td>{r.name}</td><td>{r.email}</td>
                  <td>{r.total_score}/300</td>
                  <td><span className="tier-pill substantial">{r.overall_tier_label}</span></td>
                  <td>{r.pillar_scores?.S}</td><td>{r.pillar_scores?.P}</td><td>{r.pillar_scores?.A}</td><td>{r.pillar_scores?.C}</td><td>{r.pillar_scores?.E}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }} onClick={() => onDownloadPdf(r.id)}>Download</button>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px" }} onClick={() => onResendPdf(r.id, r.email)} disabled={resendingId === r.id}>
                      {resendingId === r.id ? "Sending…" : "Resend"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {results.recordings.length > 0 && (
        <section className="search-section" data-testid="search-section-recordings">
          <h3 className="search-section-title">Recording Access Links ({results.recordings.length})</h3>
          <table className="admin-table">
            <thead><tr><th>Issued</th><th>Name</th><th>Email</th><th>Recording URL</th><th>Views</th><th>Last Viewed</th><th>Actions</th></tr></thead>
            <tbody>
              {results.recordings.map((t) => (
                <tr key={t.token}>
                  <td>{fmtDate(t.issued_at)}</td>
                  <td>{t.name}</td><td>{t.email}</td>
                  <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <a href={t.recording_url} target="_blank" rel="noopener noreferrer" style={{ color: "#d4af37" }}>{t.recording_url}</a>
                  </td>
                  <td><span className={`tier-pill ${t.view_count > 0 ? "substantial" : "out"}`}>{t.view_count || 0}</span></td>
                  <td style={{ fontSize: 11 }}>{t.last_viewed_at ? fmtDate(t.last_viewed_at).slice(0, 16) : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }} onClick={() => copyLink(t.magic_link, `s-rec-${t.token}`)}>
                      {copiedToken === `s-rec-${t.token}` ? "Copied ✓" : "Copy Link"}
                    </button>
                    <button className="admin-btn outline" style={{ fontSize: 10, padding: "6px 10px" }} onClick={() => onRevokeRecording(t.token, t.email)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}


// =====================================================
// PurchasesTab — $497 Diakonia / $5,000 Full Protocol
// =====================================================
function PurchasesTab({ purchases, adminToken, onChange }) {
  const [busyId, setBusyId] = useState("");
  const [msg, setMsg] = useState("");
  const fmtDate = (s) => (s || "").slice(0, 19).replace("T", " ");

  async function markPaid(purchaseId) {
    if (!window.confirm("Mark this purchase as PAID? This will auto-generate the report(s) and email them to the buyer.")) return;
    setBusyId(purchaseId);
    setMsg("");
    try {
      await axios.post(
        `${API}/admin/purchase/mark-paid`,
        { purchase_id: purchaseId },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMsg("Payment confirmed. Report generation queued — refresh in ~90s to see delivery status.");
      onChange && onChange();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not mark paid.");
    }
    setBusyId("");
  }

  const tierLabel = (t) => (t === "full_protocol" ? "Full ($5K)" : "Diakonia ($497)");
  const statusPill = (s) => {
    const cls = s === "delivered" ? "substantial" : s === "paid" ? "mostly" : "out";
    return <span className={`tier-pill ${cls}`}>{s}</span>;
  };

  if (!purchases || purchases.length === 0) {
    return (
      <div className="empty-state" data-testid="purchases-empty">
        <p>No purchases yet. New intakes will show up here when clients submit the intake form at <code>/intake</code>.</p>
      </div>
    );
  }

  return (
    <section data-testid="purchases-panel">
      {msg && <p style={{ color: "#f5d76e", fontSize: 13, marginBottom: 16 }} data-testid="purchases-msg">{msg}</p>}
      <table className="admin-table" data-testid="admin-table-purchases">
        <thead>
          <tr>
            <th>Date</th>
            <th>Name / Email</th>
            <th>Tier</th>
            <th>Birth Data</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {purchases.map((p) => (
            <tr key={p.id} data-testid={`purchase-row-${p.id}`}>
              <td style={{ fontSize: 11 }}>{fmtDate(p.created_at)}</td>
              <td>
                <div><strong>{p.name}</strong></div>
                <div style={{ fontSize: 11, color: "#c7bfa8" }}>{p.email}</div>
                {p.diagnostic_submission_id && (
                  <div style={{ fontSize: 10, color: "#7ec98a" }} title="Diagnostic on file">◆ Diagnostic linked</div>
                )}
              </td>
              <td>{tierLabel(p.tier)}</td>
              <td style={{ fontSize: 11 }}>
                <div>DOB: {p.birth_data?.dob || "—"}</div>
                <div>{p.birth_data?.place || "—"}</div>
                <div>{p.birth_data?.time || "—"}</div>
              </td>
              <td>{statusPill(p.status)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {p.status === "pending_payment" && (
                  <button
                    className="admin-btn"
                    style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                    onClick={() => markPaid(p.id)}
                    disabled={busyId === p.id}
                    data-testid={`purchase-mark-paid-${p.id}`}
                  >
                    {busyId === p.id ? "Working…" : "Mark Paid → Generate"}
                  </button>
                )}
                {p.diakonia_report_id && (
                  <a
                    className="admin-btn outline"
                    style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                    href={`${API}/admin/diakonia-report/${p.diakonia_report_id}/pdf?token=${encodeURIComponent(adminToken)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`purchase-dl-diakonia-${p.id}`}
                  >
                    Diakonia PDF
                  </a>
                )}
                {p.protocol_report_id && (
                  <a
                    className="admin-btn outline"
                    style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                    href={`${API}/admin/purchase/${p.id}/protocol-pdf?token=${encodeURIComponent(adminToken)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`purchase-dl-protocol-${p.id}`}
                  >
                    Protocol PDF
                  </a>
                )}
                {p.status === "delivered" && (
                  <a
                    className="admin-btn"
                    style={{ fontSize: 10, padding: "6px 10px" }}
                    href={`${API}/admin/purchase/${p.id}/combined-pdf?token=${encodeURIComponent(adminToken)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`purchase-dl-combined-${p.id}`}
                  >
                    Combined PDF
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// =====================================================
// ConversationsTab — live Architect chat transcripts + reset
// =====================================================
function ConversationsTab({ conversations, adminToken, onChange }) {
  const [selected, setSelected] = useState(null);
  const [fullConv, setFullConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [msg, setMsg] = useState("");
  const fmt = (s) => (s || "").slice(0, 19).replace("T", " ");

  async function openConversation(sessionId) {
    setSelected(sessionId);
    setFullConv(null);
    setLoadingConv(true);
    try {
      const res = await axios.get(
        `${API}/admin/architect-conversations/${sessionId}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setFullConv(res.data);
    } catch (e) {
      setMsg("Could not load conversation.");
    }
    setLoadingConv(false);
  }

  async function resetConversation(sessionId) {
    if (!window.confirm(`Reset conversation ${sessionId.slice(0, 12)}…? The visitor will start fresh next time they open the chat.`)) return;
    setMsg("");
    try {
      await axios.delete(
        `${API}/admin/architect-conversations/${sessionId}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMsg(`Reset ${sessionId.slice(0, 12)}…`);
      if (selected === sessionId) {
        setSelected(null);
        setFullConv(null);
      }
      onChange && onChange();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not reset.");
    }
  }

  if (!conversations || conversations.length === 0) {
    return (
      <div className="empty-state" data-testid="conversations-empty">
        <p>No Architect conversations yet. When visitors open the floating chat on the funnel, sessions will show up here.</p>
      </div>
    );
  }

  return (
    <section data-testid="conversations-panel">
      {msg && <p style={{ color: "#f5d76e", fontSize: 13, marginBottom: 16 }} data-testid="conversations-msg">{msg}</p>}
      <div className="conversations-layout">
        <div className="conversations-list">
          <table className="admin-table" data-testid="admin-table-conversations">
            <thead>
              <tr>
                <th>Last Active</th>
                <th>Visitor</th>
                <th>Turns</th>
                <th>Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((c) => {
                const lastTurn = c.preview && c.preview.length > 0 ? c.preview[c.preview.length - 1] : null;
                const previewSnippet = lastTurn ? lastTurn.content : "(no messages)";
                return (
                  <tr
                    key={c.session_id}
                    className={selected === c.session_id ? "conversation-row-active" : ""}
                    data-testid={`conversation-row-${c.session_id}`}
                  >
                    <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmt(c.last_at)}</td>
                    <td style={{ fontSize: 12 }}>
                      <div><strong>{c.visitor_name || "—"}</strong></div>
                      <div style={{ color: "#c7bfa8" }}>{c.visitor_email || "anonymous"}</div>
                    </td>
                    <td>{c.turn_count}</td>
                    <td style={{ fontSize: 11, maxWidth: 320 }}>
                      <span style={{ color: lastTurn?.role === "user" ? "#f5d76e" : "#c7bfa8" }}>
                        {lastTurn?.role === "user" ? "→ " : "◈ "}
                      </span>
                      {previewSnippet.slice(0, 140)}{previewSnippet.length > 140 ? "…" : ""}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="admin-btn"
                        style={{ fontSize: 10, padding: "6px 10px", marginRight: 6 }}
                        onClick={() => openConversation(c.session_id)}
                        data-testid={`conversation-view-${c.session_id}`}
                      >
                        View
                      </button>
                      <button
                        className="admin-btn outline"
                        style={{ fontSize: 10, padding: "6px 10px" }}
                        onClick={() => resetConversation(c.session_id)}
                        data-testid={`conversation-reset-${c.session_id}`}
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="conversation-detail" data-testid="conversation-detail">
            <div className="conversation-detail-head">
              <div>
                <p className="conversation-detail-eyebrow">Session · {selected.slice(0, 18)}…</p>
                <h3 className="conversation-detail-title">
                  {fullConv?.visitor_name || "Anonymous"}
                </h3>
                <p className="conversation-detail-sub">
                  {fullConv?.visitor_email || "no email captured"} · started {fmt(fullConv?.started_at)}
                </p>
              </div>
              <button
                className="admin-btn outline"
                style={{ fontSize: 11, padding: "6px 10px" }}
                onClick={() => { setSelected(null); setFullConv(null); }}
                data-testid="conversation-close"
              >
                Close ×
              </button>
            </div>
            <div className="conversation-transcript" data-testid="conversation-transcript">
              {loadingConv && <p style={{ color: "#c7bfa8" }}>Loading transcript…</p>}
              {!loadingConv && fullConv?.turns?.map((t, i) => (
                <div
                  key={i}
                  className={`conv-turn conv-turn-${t.role}`}
                  data-testid={`conv-turn-${i}`}
                >
                  <div className="conv-turn-meta">
                    <span className="conv-turn-role">{t.role === "user" ? "Visitor" : "Architect"}</span>
                    <span className="conv-turn-time">{fmt(t.at)}</span>
                  </div>
                  <div className="conv-turn-body">
                    {(t.content || "").split("\n").map((line, j) => (
                      <p key={j}>{line}</p>
                    ))}
                  </div>
                  {t.actions && t.actions.length > 0 && (
                    <div className="conv-turn-actions">
                      {t.actions.map((a, k) => (
                        <span className="conv-action-tag" key={k}>
                          {a.type}{a.reason ? ` · ${a.reason}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

